import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  patientsApi,
  studiesApi,
  workOrdersApi,
  priceListsApi,
  branchesApi,
  profilesApi,
  doctorsApi,
  companiesApi,
} from "@/lib/api";
import { toast } from "sonner";
import {
  User,
  Search,
  FlaskConical,
  Trash2,
  CreditCard,
  Receipt,
  Tag,
  CheckCircle,
  DollarSign,
  Eye,
  Building2,
  Layers,
  Printer,
} from "lucide-react";
import MobilePDFViewer from "@/components/MobilePDFViewer";
import { printRecibo, printEtiquetas, printTicket58 } from "@/lib/qzTray";

export default function CreateOrder() {
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [studies, setStudies] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [selectedPriceList, setSelectedPriceList] = useState("");
  const [selectedStudies, setSelectedStudies] = useState([]);
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showReceiptPreview, setShowReceiptPreview] = useState(false);
  const [showLabelPreview, setShowLabelPreview] = useState(false);
  const [showTicketPreview, setShowTicketPreview] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState("");
  const [labelUrl, setLabelUrl] = useState("");
  const [ticketUrl, setTicketUrl] = useState("");
  const [createdOrder, setCreatedOrder] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [selectedDoctorName, setSelectedDoctorName] = useState("");
  const [paymentData, setPaymentData] = useState({
    discount: "",
    discountPercent: "",
    amountPaid: "",
    pendingBalance: 0,
  });
  
  // Estado para navegación con teclado y dropdown
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchInputRef = useRef(null);
  const resultsContainerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [patientId]);

  useEffect(() => {
    if (selectedPriceList) {
      fetchStudiesWithPrices(selectedPriceList);
      fetchProfilesWithPrices(selectedPriceList);
    }
  }, [selectedPriceList]);

  const fetchData = async () => {
    try {
      const [patientRes, priceListsRes, branchesRes, doctorsRes, companiesRes] = await Promise.all([
        patientsApi.getOne(patientId),
        priceListsApi.getAll(),
        branchesApi.getAll(),
        doctorsApi.getAll(),
        companiesApi.getAll(),
      ]);
      setPatient(patientRes.data);
      
      // Ordenar listas de precios: PARTICULAR primero, luego el resto alfabéticamente
      const sortedPriceLists = [...priceListsRes.data].sort((a, b) => {
        // PARTICULAR siempre primero
        if (a.name?.toUpperCase() === 'PARTICULAR') return -1;
        if (b.name?.toUpperCase() === 'PARTICULAR') return 1;
        // El resto alfabéticamente
        return (a.name || '').localeCompare(b.name || '');
      });
      setPriceLists(sortedPriceLists);
      
      setBranches(branchesRes.data);
      setDoctors(doctorsRes.data.filter(d => d.active !== false));
      
      // Actualizar fecha/hora actual
      setCurrentDateTime(new Date());
      
      // Leer el doctor_id del query parameter (viene del formulario NewPatient)
      const doctorIdFromUrl = searchParams.get('doctor_id');
      if (doctorIdFromUrl) {
        setSelectedDoctorId(doctorIdFromUrl);
        const doctor = doctorsRes.data.find(d => d.id === doctorIdFromUrl);
        setSelectedDoctorName(doctor?.name || 'A QUIEN CORRESPONDA');
      } else {
        setSelectedDoctorId("");
        setSelectedDoctorName('A QUIEN CORRESPONDA');
      }
      
      // Check if patient has a company with associated branch/price_list
      const patientCompany = patientRes.data.company_id 
        ? companiesRes.data.find(c => c.id === patientRes.data.company_id)
        : null;
      
      if (patientCompany?.price_list_id) {
        // Auto-select the company's price list
        const companyPriceList = sortedPriceLists.find(p => p.id === patientCompany.price_list_id);
        if (companyPriceList) {
          setSelectedPriceList(companyPriceList.id);
        } else {
          // Fallback to default
          const defaultList = sortedPriceLists.find(p => p.is_default) || sortedPriceLists.find(p => p.name?.toUpperCase() === 'PARTICULAR');
          if (defaultList) setSelectedPriceList(defaultList.id);
          else if (sortedPriceLists.length > 0) setSelectedPriceList(sortedPriceLists[0].id);
        }
      } else {
        // Auto-select by matching company name to price list name
        const matchingPriceList = patientCompany 
          ? sortedPriceLists.find(p => p.name?.toUpperCase() === patientCompany.name?.toUpperCase())
          : null;
        if (matchingPriceList) {
          setSelectedPriceList(matchingPriceList.id);
        } else {
          const defaultList = sortedPriceLists.find(p => p.is_default) || sortedPriceLists.find(p => p.name?.toUpperCase() === 'PARTICULAR');
          if (defaultList) setSelectedPriceList(defaultList.id);
          else if (sortedPriceLists.length > 0) setSelectedPriceList(sortedPriceLists[0].id);
        }
      }
      
      if (patientCompany?.branch_id) {
        // Auto-select the company's branch
        const companyBranch = branchesRes.data.find(b => b.id === patientCompany.branch_id);
        if (companyBranch) {
          setSelectedBranch(companyBranch.name || companyBranch);
        } else if (branchesRes.data.length > 0) {
          setSelectedBranch(branchesRes.data[0].name || branchesRes.data[0]);
        }
      } else {
        // Auto-select by matching company name to branch name
        const matchingBranch = patientCompany
          ? branchesRes.data.find(b => b.name?.toUpperCase() === patientCompany.name?.toUpperCase())
          : null;
        if (matchingBranch) {
          setSelectedBranch(matchingBranch.name || matchingBranch);
        } else if (branchesRes.data.length > 0) {
          setSelectedBranch(branchesRes.data[0].name || branchesRes.data[0]);
        }
      }
    } catch (error) {
      toast.error("Error al cargar datos");
      navigate("/reception/new-patient");
    } finally {
      setLoading(false);
    }
  };

  const fetchStudiesWithPrices = async (priceListId) => {
    try {
      const response = await studiesApi.getWithPrices(priceListId);
      setStudies(response.data);
    } catch (error) {
      console.error("Error loading studies:", error);
    }
  };

  const fetchProfilesWithPrices = async (priceListId) => {
    try {
      const response = await profilesApi.getWithPrices(priceListId);
      setProfiles(response.data);
    } catch (error) {
      console.error("Error loading profiles:", error);
    }
  };

  const handleStudyToggle = (study) => {
    setSelectedStudies((prev) => {
      const exists = prev.find((s) => s.study_id === study.id);
      if (exists) {
        return prev.filter((s) => s.study_id !== study.id);
      }
      return [
        ...prev,
        {
          study_id: study.id,
          study_name: study.name,
          study_code: study.code,
          price: study.price || 0,
          container_name: study.container_name || "",
          sample_type_name: study.sample_type_name || "",
        },
      ];
    });
  };

  const handleProfileToggle = (profile) => {
    setSelectedProfiles((prev) => {
      const exists = prev.find((p) => p.profile_id === profile.id);
      if (exists) {
        return prev.filter((p) => p.profile_id !== profile.id);
      }
      return [
        ...prev,
        {
          profile_id: profile.id,
          profile_name: profile.name,
          profile_code: profile.code,
          price: profile.price || 0,
          study_ids: profile.study_ids || [],
        },
      ];
    });
  };

  const removeStudy = (studyId) => {
    setSelectedStudies((prev) => prev.filter((s) => s.study_id !== studyId));
  };

  const removeProfile = (profileId) => {
    setSelectedProfiles((prev) => prev.filter((p) => p.profile_id !== profileId));
  };

  // Calculate totals including profiles
  const studiesSubtotal = selectedStudies.reduce((sum, s) => sum + s.price, 0);
  const profilesSubtotal = selectedProfiles.reduce((sum, p) => sum + p.price, 0);
  const subtotal = studiesSubtotal + profilesSubtotal;
  const discountAmount = parseFloat(paymentData.discount) || 0;
  const total = subtotal - discountAmount;
  const amountPaidValue = parseFloat(paymentData.amountPaid) || 0;
  const pendingBalance = Math.max(0, total - amountPaidValue);

  // Handle discount change - calculate percent and pending balance
  const handleDiscountChange = (value) => {
    const discount = value === "" ? "" : parseFloat(value) || 0;
    const discountNum = parseFloat(discount) || 0;
    const percent = subtotal > 0 ? ((discountNum / subtotal) * 100).toFixed(2) : 0;
    const newTotal = subtotal - discountNum;
    const currentPaid = parseFloat(paymentData.amountPaid) || 0;
    setPaymentData((prev) => ({
      ...prev,
      discount: value,
      discountPercent: discountNum > 0 ? percent : "",
      pendingBalance: Math.max(0, newTotal - currentPaid),
    }));
  };

  // Handle discount percent change - calculate discount amount
  const handleDiscountPercentChange = (value) => {
    const percent = value === "" ? "" : parseFloat(value) || 0;
    const percentNum = parseFloat(percent) || 0;
    const discount = (subtotal * percentNum) / 100;
    const newTotal = subtotal - discount;
    const currentPaid = parseFloat(paymentData.amountPaid) || 0;
    setPaymentData((prev) => ({
      ...prev,
      discountPercent: value,
      discount: percentNum > 0 ? discount.toFixed(2) : "",
      pendingBalance: Math.max(0, newTotal - currentPaid),
    }));
  };

  // Handle amount paid change - calculate pending balance and discount bidirectionally
  const handleAmountPaidChange = (value) => {
    const amountPaid = value === "" ? "" : parseFloat(value) || 0;
    const amountPaidNum = parseFloat(amountPaid) || 0;
    const currentDiscount = parseFloat(paymentData.discount) || 0;
    const currentTotal = subtotal - currentDiscount;
    const newPendingBalance = Math.max(0, currentTotal - amountPaidNum);
    
    setPaymentData((prev) => ({
      ...prev,
      amountPaid: value,
      pendingBalance: newPendingBalance,
    }));
  };

  const handleOpenPayment = () => {
    if (selectedStudies.length === 0 && selectedProfiles.length === 0) {
      toast.error("Seleccione al menos un estudio o perfil");
      return;
    }
    // Initialize with empty fields, pending balance equals total
    setPaymentData({ discount: "", discountPercent: "", amountPaid: "", pendingBalance: subtotal });
    setShowPayment(true);
  };

  const handlePayment = async () => {
    const amountPaidNum = parseFloat(paymentData.amountPaid) || 0;
    const currentPendingBalance = Math.max(0, total - amountPaidNum);

    setLoading(true);
    try {
      // Expand profile studies and combine with individual studies
      let allStudies = [...selectedStudies];
      
      // For each selected profile, get its studies from the profiles data
      for (const selectedProfile of selectedProfiles) {
        const profileData = profiles.find(p => p.id === selectedProfile.profile_id);
        if (profileData && profileData.study_ids) {
          for (const studyId of profileData.study_ids) {
            // Find the study details
            const studyDetails = studies.find(s => s.id === studyId);
            if (studyDetails) {
              // Check if this study is not already added individually
              const alreadyAdded = allStudies.some(s => s.study_id === studyId);
              if (!alreadyAdded) {
                allStudies.push({
                  study_id: studyDetails.id,
                  study_name: studyDetails.name,
                  study_code: studyDetails.code,
                  price: 0, // Price is included in profile, individual study price is 0
                  container_name: studyDetails.container_name || "",
                  sample_type_name: studyDetails.sample_type_name || "",
                  from_profile: selectedProfile.profile_name, // Mark which profile it comes from
                });
              }
            }
          }
        }
      }

      // Prepare profiles data for the order
      const profilesData = selectedProfiles.map(p => ({
        profile_id: p.profile_id,
        profile_name: p.profile_name,
        profile_code: p.profile_code,
        price: p.price,
        study_ids: p.study_ids || [],
      }));

      const orderData = {
        patient_id: patientId,
        doctor_id: selectedDoctorId || null,
        doctor_name: selectedDoctorName !== 'A QUIEN CORRESPONDA' ? selectedDoctorName : null,
        company_id: patient.company_id || null,
        branch: selectedBranch || null,
        studies: allStudies,
        profiles: profilesData,
        subtotal,
        discount: discountAmount,
        total,
        amount_paid: amountPaidNum,
        pending_balance: currentPendingBalance,
        notes: null,
        status: "pending",
      };

      const response = await workOrdersApi.create(orderData);
      setCreatedOrder(response.data);
      setShowPayment(false);
      setShowSuccess(true);
      toast.success("Orden creada exitosamente");
    } catch (error) {
      toast.error("Error al crear orden");
    } finally {
      setLoading(false);
    }
  };

  const previewReceipt = async () => {
    try {
      const response = await workOrdersApi.downloadReceipt(createdOrder.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setReceiptUrl(url);
      setShowReceiptPreview(true);
    } catch (error) {
      toast.error("Error al generar vista previa");
    }
  };

  const previewTicket = async () => {
    try {
      const response = await workOrdersApi.downloadReceiptTicket(createdOrder.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setTicketUrl(url);
      setShowTicketPreview(true);
    } catch (error) {
      toast.error("Error al generar vista previa del ticket");
    }
  };

  const previewLabel = async () => {
    try {
      const response = await workOrdersApi.downloadLabel(createdOrder.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setLabelUrl(url);
      setShowLabelPreview(true);
    } catch (error) {
      toast.error("Error al generar vista previa");
    }
  };

  const printDocument = (url) => {
    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const filteredStudies = studies.filter(
    (study) =>
      study.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      study.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredProfiles = profiles.filter(
    (profile) =>
      profile.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      profile.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Combinar perfiles y estudios para navegación con teclado
  const combinedResults = [
    ...filteredProfiles.map(p => ({ ...p, type: 'profile' })),
    ...filteredStudies.map(s => ({ ...s, type: 'study' }))
  ];

  // Mostrar dropdown cuando hay búsqueda y destacar el primer elemento
  useEffect(() => {
    const hasResults = searchTerm.length > 0 && combinedResults.length > 0;
    setShowDropdown(hasResults);
    // Destacar el primer elemento cuando hay resultados
    setHighlightedIndex(hasResults ? 0 : -1);
  }, [searchTerm, combinedResults.length]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll al elemento resaltado
  useEffect(() => {
    if (highlightedIndex >= 0 && resultsContainerRef.current) {
      const highlightedElement = resultsContainerRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex]);

  // Manejador de teclado para navegación
  const handleKeyDown = (e) => {
    if (!searchTerm || combinedResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setShowDropdown(true);
        setHighlightedIndex(prev => 
          prev < combinedResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setShowDropdown(true);
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : combinedResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < combinedResults.length) {
          const item = combinedResults[highlightedIndex];
          if (item.type === 'profile') {
            handleProfileToggle(item);
          } else {
            handleStudyToggle(item);
          }
        } else if (combinedResults.length > 0) {
          // Si no hay nada resaltado pero hay resultados, selecciona el primero
          const item = combinedResults[0];
          if (item.type === 'profile') {
            handleProfileToggle(item);
          } else {
            handleStudyToggle(item);
          }
        }
        // Limpiar búsqueda y enfocar para seguir agregando
        setSearchTerm("");
        setHighlightedIndex(-1);
        searchInputRef.current?.focus();
        break;
      case 'Escape':
        setHighlightedIndex(-1);
        setShowDropdown(false);
        setSearchTerm("");
        break;
      default:
        break;
    }
  };

  // Seleccionar item del dropdown
  const handleSelectItem = (item) => {
    if (item.type === 'profile') {
      handleProfileToggle(item);
    } else {
      handleStudyToggle(item);
    }
    setSearchTerm("");
    setHighlightedIndex(-1);
    searchInputRef.current?.focus();
  };

  if (loading && !patient) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const genderMap = { male: "Masculino", female: "Femenino", other: "Otro" };

  return (
    <div className="space-y-6" data-testid="create-order-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
          Crear Orden de Análisis
        </h1>
        <p className="text-muted-foreground">
          Seleccione los estudios para el paciente
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Patient Info - Left Side */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5" />
              Datos del Paciente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {patient && (
              <>
                <div className="p-4 bg-secondary/50 rounded-lg">
                  <p className="text-lg font-semibold">
                    {patient.first_name} {patient.last_name}
                  </p>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Edad:</span>
                    <span className="font-medium">{patient.age || '-'} años</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sexo:</span>
                    <span className="font-medium">{genderMap[patient.gender] || patient.gender}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Fecha:</span>
                    <span className="font-medium">
                      {currentDateTime.toLocaleDateString('es-MX')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Hora:</span>
                    <span className="font-medium">
                      {currentDateTime.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Médico:</span>
                    <span className="font-medium">
                      {selectedDoctorName || 'A QUIEN CORRESPONDA'}
                    </span>
                  </div>
                </div>

                <Separator />

                {/* Selected Studies Summary */}
                <div>
                  <h4 className="font-semibold mb-2">Estudios Seleccionados</h4>
                  {selectedStudies.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No hay estudios seleccionados
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {selectedStudies.map((study) => (
                        <div
                          key={study.study_id}
                          className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm"
                        >
                          <div className="truncate flex-1">
                            <span className="font-mono font-bold text-xs">{study.study_code}</span>
                            <span className="ml-2">{study.study_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              ${study.price.toLocaleString()}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => removeStudy(study.study_id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {/* Selected profiles */}
                      {selectedProfiles.map((profile) => (
                        <div
                          key={`profile-${profile.profile_id}`}
                          className="flex items-center justify-between p-2 bg-purple-50 rounded text-sm border-l-4 border-l-purple-500"
                        >
                          <div className="truncate flex-1 flex items-center gap-2">
                            <Layers className="h-4 w-4 text-purple-600" />
                            <span className="font-mono font-bold text-xs">{profile.profile_code}</span>
                            <span>{profile.profile_name}</span>
                            <Badge variant="secondary" className="text-xs">
                              {profile.study_ids?.length || 0} estudios
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-purple-700">
                              ${profile.price.toLocaleString()}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => removeProfile(profile.profile_id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                {/* Total */}
                <div className="space-y-2">
                  {studiesSubtotal > 0 && (
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Estudios ({selectedStudies.length}):</span>
                      <span>${studiesSubtotal.toLocaleString()}</span>
                    </div>
                  )}
                  {profilesSubtotal > 0 && (
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Perfiles ({selectedProfiles.length}):</span>
                      <span>${profilesSubtotal.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span>${subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg">
                    <span>Total:</span>
                    <span className="text-emerald-600">${subtotal.toLocaleString()}</span>
                  </div>
                </div>

                <Button
                  className="w-full"
                  onClick={handleOpenPayment}
                  disabled={selectedStudies.length === 0 && selectedProfiles.length === 0}
                  data-testid="open-payment-btn"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Guardar y Pagar
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Studies Selection - Right Side */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-3">
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FlaskConical className="h-5 w-5" />
                  Buscar Análisis
                </CardTitle>
                <Badge variant="secondary">
                  {selectedStudies.length + selectedProfiles.length} seleccionados
                </Badge>
              </div>
              <div className="flex gap-3 flex-wrap">
                {/* Barra de búsqueda con dropdown en tiempo real */}
                <div className="relative flex-1 min-w-[300px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                  <Input
                    ref={searchInputRef}
                    placeholder="Buscar estudio o perfil... (↑↓ Enter)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => searchTerm && combinedResults.length > 0 && setShowDropdown(true)}
                    className="pl-10 pr-4"
                    data-testid="search-studies"
                    autoComplete="off"
                  />
                  
                  {/* Dropdown de resultados en tiempo real */}
                  {showDropdown && combinedResults.length > 0 && (
                    <div 
                      ref={dropdownRef}
                      className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-[400px] overflow-hidden"
                    >
                      <div ref={resultsContainerRef} className="overflow-y-auto max-h-[400px]">
                        {/* Perfiles */}
                        {filteredProfiles.length > 0 && (
                          <div className="p-2">
                            <div className="text-xs font-semibold text-purple-600 px-2 py-1 flex items-center gap-1">
                              <Layers className="h-3 w-3" />
                              Perfiles ({filteredProfiles.length})
                            </div>
                            {filteredProfiles.map((profile, idx) => {
                              const isSelected = selectedProfiles.some(p => p.profile_id === profile.id);
                              const isHighlighted = highlightedIndex === idx;
                              return (
                                <div
                                  key={`dropdown-profile-${profile.id}`}
                                  data-index={idx}
                                  onClick={() => handleSelectItem({ ...profile, type: 'profile' })}
                                  className={`flex items-center justify-between px-3 py-2 cursor-pointer rounded transition-colors ${
                                    isHighlighted 
                                      ? 'bg-primary text-primary-foreground' 
                                      : isSelected 
                                      ? 'bg-purple-50' 
                                      : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <Checkbox checked={isSelected} className={`pointer-events-none ${isHighlighted ? 'border-white' : ''}`} />
                                    <div>
                                      <span className={`font-mono text-sm font-bold ${isHighlighted ? 'text-primary-foreground/80' : 'text-purple-700'}`}>{profile.code}</span>
                                      <span className="ml-2 text-sm">{profile.name}</span>
                                      <span className={`ml-2 text-xs ${isHighlighted ? 'text-primary-foreground/60' : 'text-gray-400'}`}>({profile.study_ids?.length || 0} estudios)</span>
                                    </div>
                                  </div>
                                  <span className={`font-semibold ${isHighlighted ? 'text-primary-foreground' : 'text-purple-700'}`}>${(profile.price || 0).toLocaleString()}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Separador */}
                        {filteredProfiles.length > 0 && filteredStudies.length > 0 && (
                          <Separator />
                        )}
                        
                        {/* Estudios */}
                        {filteredStudies.length > 0 && (
                          <div className="p-2">
                            <div className="text-xs font-semibold text-blue-600 px-2 py-1 flex items-center gap-1">
                              <FlaskConical className="h-3 w-3" />
                              Estudios ({filteredStudies.length})
                            </div>
                            {filteredStudies.map((study, idx) => {
                              const isSelected = selectedStudies.some(s => s.study_id === study.id);
                              const combinedIdx = filteredProfiles.length + idx;
                              const isHighlighted = highlightedIndex === combinedIdx;
                              return (
                                <div
                                  key={`dropdown-study-${study.id}`}
                                  data-index={combinedIdx}
                                  onClick={() => handleSelectItem({ ...study, type: 'study' })}
                                  className={`flex items-center justify-between px-3 py-2 cursor-pointer rounded transition-colors ${
                                    isHighlighted 
                                      ? 'bg-primary text-primary-foreground' 
                                      : isSelected 
                                      ? 'bg-blue-50' 
                                      : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <Checkbox checked={isSelected} className={`pointer-events-none ${isHighlighted ? 'border-white' : ''}`} />
                                    <div>
                                      <span className={`font-mono text-sm font-bold ${isHighlighted ? 'text-primary-foreground/80' : 'text-blue-700'}`}>{study.code}</span>
                                      <span className="ml-2 text-sm">{study.name}</span>
                                    </div>
                                  </div>
                                  <span className={`font-semibold ${isHighlighted ? 'text-primary-foreground' : 'text-green-600'}`}>${(study.price || 0).toLocaleString()}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Sin resultados */}
                        {combinedResults.length === 0 && searchTerm && (
                          <div className="p-4 text-center text-gray-500">
                            No se encontraron resultados
                          </div>
                        )}
                      </div>
                      
                      {/* Footer con instrucciones */}
                      <div className="border-t bg-gray-50 px-3 py-2 text-xs text-gray-500 flex items-center justify-between">
                        <span>↑↓ navegar • Enter seleccionar • Esc cerrar</span>
                        <span>{combinedResults.length} resultado{combinedResults.length !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  )}
                </div>
                
                <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                  <SelectTrigger className="w-40" data-testid="branch-select">
                    <Building2 className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Sucursal" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((branch) => (
                      <SelectItem key={branch.name || branch} value={branch.name || branch}>
                        {branch.name || branch}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedPriceList} onValueChange={setSelectedPriceList}>
                  <SelectTrigger className="w-48" data-testid="price-list-select">
                    <DollarSign className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Lista de Precios" />
                  </SelectTrigger>
                  <SelectContent>
                    {priceLists.map((list) => (
                      <SelectItem key={list.id} value={list.id}>
                        {list.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {priceLists.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No hay listas de precios configuradas.</p>
                <Button variant="link" onClick={() => navigate('/catalogs/price-lists')}>
                  Ir a configurar listas de precios
                </Button>
              </div>
            ) : selectedStudies.length === 0 && selectedProfiles.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Search className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p className="text-lg">Escriba en la barra de búsqueda</p>
                <p className="text-sm">para encontrar perfiles o estudios</p>
                <p className="text-xs mt-2 text-muted-foreground/70">
                  Use ↑↓ para navegar y Enter para seleccionar
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">Estudios Agregados</h3>
                  <Badge variant="outline">{selectedStudies.length + selectedProfiles.length} items</Badge>
                </div>
                
                <ScrollArea className="h-[calc(100vh-300px)]">
                  <div className="space-y-2">
                    {/* Perfiles seleccionados */}
                    {selectedProfiles.map((profile) => (
                      <div
                        key={`selected-profile-${profile.profile_id}`}
                        className="flex items-center justify-between p-3 rounded-lg border bg-purple-50 border-purple-200"
                      >
                        <div className="flex items-center gap-3">
                          <Layers className="h-5 w-5 text-purple-600" />
                          <div>
                            <p className="font-medium">
                              <span className="font-mono font-bold text-purple-700 mr-2">{profile.profile_code}</span>
                              {profile.profile_name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Perfil • {profile.study_ids?.length || 0} estudios incluidos
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-lg text-purple-700">
                            ${(profile.price || 0).toLocaleString()}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleProfileToggle({ id: profile.profile_id, ...profile })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    {/* Estudios seleccionados */}
                    {selectedStudies.map((study) => (
                      <div
                        key={`selected-study-${study.study_id}`}
                        className="flex items-center justify-between p-3 rounded-lg border bg-blue-50 border-blue-200"
                      >
                        <div className="flex items-center gap-3">
                          <FlaskConical className="h-5 w-5 text-blue-600" />
                          <div>
                            <p className="font-medium">
                              <span className="font-mono font-bold text-blue-700 mr-2">{study.study_code}</span>
                              {study.study_name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {study.container_name && `Recipiente: ${study.container_name}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-lg">
                            ${(study.price || 0).toLocaleString()}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleStudyToggle({ id: study.study_id, ...study })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Ventana de Pago
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-muted rounded-lg space-y-3">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span className="font-medium">${subtotal.toLocaleString()}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Descuento %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="0"
                    value={paymentData.discountPercent}
                    onChange={(e) => handleDiscountPercentChange(e.target.value)}
                    data-testid="payment-discount-percent"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Descuento $</Label>
                  <Input
                    type="number"
                    min="0"
                    max={subtotal}
                    placeholder="0"
                    value={paymentData.discount}
                    onChange={(e) => handleDiscountChange(e.target.value)}
                    data-testid="payment-discount"
                  />
                </div>
              </div>
              
              <Separator />
              
              <div className="flex justify-between font-bold text-lg">
                <span>Total a Pagar:</span>
                <span className="text-emerald-600">${total.toLocaleString()}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Monto Recibido ($)</Label>
              <Input
                type="number"
                min="0"
                placeholder="0"
                value={paymentData.amountPaid}
                onChange={(e) => handleAmountPaidChange(e.target.value)}
                className="text-lg font-bold"
                data-testid="payment-amount"
              />
            </div>

            {/* Pending Balance */}
            <div className={`p-3 rounded-lg ${pendingBalance > 0 ? 'bg-orange-50 border border-orange-200' : 'bg-emerald-50'}`}>
              <div className="flex justify-between text-sm">
                <span className={pendingBalance > 0 ? 'text-orange-700' : 'text-emerald-700'}>
                  Saldo Pendiente:
                </span>
                <span className={`font-bold ${pendingBalance > 0 ? 'text-orange-700' : 'text-emerald-700'}`}>
                  ${pendingBalance.toLocaleString()}
                </span>
              </div>
            </div>

            {amountPaidValue > total && (
              <div className="p-3 bg-emerald-50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-emerald-700">Cambio:</span>
                  <span className="font-bold text-emerald-700">
                    ${(amountPaidValue - total).toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayment(false)}>
              Cancelar
            </Button>
            <Button onClick={handlePayment} disabled={loading} data-testid="confirm-payment-btn">
              {loading ? "Procesando..." : "Confirmar Pago"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      {/* Success Dialog - No se puede cerrar sin acción */}
      <Dialog open={showSuccess} onOpenChange={() => {}}>
        <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600">
              <CheckCircle className="h-5 w-5" />
              Orden Creada Exitosamente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-center text-muted-foreground">
              La orden ha sido registrada. Puede ver la vista previa e imprimir los documentos.
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="w-full"
                onClick={previewReceipt}
                data-testid="preview-receipt-btn"
              >
                <Eye className="h-4 w-4 mr-2" />
                Ver Recibo
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={previewLabel}
                data-testid="preview-label-btn"
              >
                <Eye className="h-4 w-4 mr-2" />
                Ver Etiquetas
              </Button>
            </div>
            <Button
              variant="outline"
              className="w-full border-teal-600 text-teal-700 hover:bg-teal-50"
              onClick={previewTicket}
              data-testid="preview-ticket58-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Ticket 58mm (Impresora Térmica)
            </Button>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button onClick={() => navigate("/reception/new-patient")} className="w-full sm:w-auto" data-testid="new-patient-btn">
              Nuevo Paciente
            </Button>
            <Button variant="outline" onClick={() => navigate("/reception/history")} className="w-full sm:w-auto">
              Ver Historial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Preview Dialog - Compatible con Android */}
      <Dialog open={showReceiptPreview} onOpenChange={setShowReceiptPreview}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden flex flex-col" style={{ height: 'auto', maxHeight: '90vh' }}>
          <DialogHeader className="px-4 py-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Recibo de Pago
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden min-h-0">
            {receiptUrl && (
              <MobilePDFViewer 
                pdfUrl={receiptUrl}
                title="Recibo de Pago"
                filename={`recibo-${createdOrder?.folio || 'orden'}.pdf`}
                onPrint={() => setShowReceiptPreview(false)}
                onDirectPrint={(url) => printRecibo(url)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket 58mm Preview Dialog */}
      <Dialog open={showTicketPreview} onOpenChange={setShowTicketPreview}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden flex flex-col" style={{ height: 'auto', maxHeight: '90vh' }}>
          <DialogHeader className="px-4 py-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5" />
              Ticket 58mm (Impresora Térmica)
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden min-h-0">
            {ticketUrl && (
              <MobilePDFViewer 
                pdfUrl={ticketUrl}
                title="Ticket 58mm"
                filename={`ticket-${createdOrder?.folio || 'orden'}.pdf`}
                onPrint={() => setShowTicketPreview(false)}
                initialScale={2.0}
                onDirectPrint={(url) => printTicket58(url)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showLabelPreview} onOpenChange={setShowLabelPreview}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden flex flex-col" style={{ height: 'auto', maxHeight: '90vh' }}>
          <DialogHeader className="px-4 py-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Etiquetas de Muestras
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden min-h-0">
            {labelUrl && (
              <MobilePDFViewer 
                pdfUrl={labelUrl}
                title="Etiquetas de Muestras"
                filename={`etiquetas-${createdOrder?.folio || 'orden'}.pdf`}
                onPrint={() => setShowLabelPreview(false)}
                isLabel={true}
                onDirectPrint={(url) => printEtiquetas(url)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
